import { useEffect, useId, useState } from 'react';

import { Button, Divider, Skeleton } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Textarea } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { respostasLimpas } from '@/data/fichaImpressa';
import { textoDaTranscricao } from '@/data/notaDeIa';
import type { NotaDeIa } from '@/data/notaDeIa';
import { dados } from '@/data/porta';
import type { CampoDeFicha, RespostaDaFicha } from '@/data/tipos';
import { BlocoDeResposta } from '@/screens/fichas/BlocoDeResposta';

export interface RevisarNotaModalProps {
    nota: NotaDeIa;
    onFechar: () => void;
    onAprovada: () => void;
}

interface Leitura {
    nome: string;
    campos: CampoDeFicha[];
    transcricao: string;
}

/**
 * A revisão da nota da IA (regra 8): o profissional vê cada campo que a IA
 * sugeriu, o resumo e a transcrição, muda o que quiser e só então aprova. É a
 * aprovação que grava a ficha no prontuário; fechar sem aprovar não grava nada.
 */
export function RevisarNotaModal({ nota, onFechar, onAprovada }: RevisarNotaModalProps) {
    const aviso = useAviso();
    const idResumo = useId();
    const [leitura, setLeitura] = useState<Leitura | null>(null);
    const [erroDaLeitura, setErroDaLeitura] = useState<string | null>(null);
    const [respostas, setRespostas] = useState<Record<string, RespostaDaFicha>>(() => ({ ...(nota.sugestao ?? {}) }));
    const [resumo, setResumo] = useState(nota.resumoSugerido ?? '');
    const [erro, setErro] = useState<string | null>(null);
    const [aprovando, setAprovando] = useState(false);

    useEffect(() => {
        let vivo = true;
        Promise.all([dados().modelosDeFicha(), dados().partesDaNotaDeIa(nota.id)])
            .then(([modelos, partes]) => {
                if (!vivo) return;
                const modelo = modelos.find((m) => m.id === nota.modeloId);
                if (!modelo) {
                    setErroDaLeitura('O modelo desta nota foi excluído. Descarte a nota e grave de novo.');
                    return;
                }
                setLeitura({ nome: modelo.nome, campos: modelo.campos, transcricao: textoDaTranscricao(partes) });
            })
            .catch((e: unknown) => {
                if (vivo) setErroDaLeitura(e instanceof Error ? e.message : 'Não foi possível ler a nota.');
            });
        return () => { vivo = false; };
    }, [nota.id, nota.modeloId]);

    const mudou = JSON.stringify(respostasLimpas(respostas)) !== JSON.stringify(respostasLimpas(nota.sugestao ?? {}))
        || resumo.trim() !== (nota.resumoSugerido ?? '').trim();

    const aprovar = async () => {
        const limpas = respostasLimpas(respostas);
        if (!resumo.trim()) {
            setErro('Escreva o resumo do atendimento antes de aprovar.');
            return;
        }
        if (Object.keys(limpas).length === 0) {
            setErro('Preencha ao menos um campo da ficha antes de aprovar.');
            return;
        }
        setAprovando(true);
        setErro(null);
        try {
            await dados().aprovarNotaDeIa(nota.id, limpas, resumo.trim());
            aviso({ title: 'Nota aprovada', description: 'A ficha e o resumo estão no prontuário do paciente.' });
            onAprovada();
            onFechar();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível aprovar a nota.');
        } finally {
            setAprovando(false);
        }
    };

    return (
        <Modal
            open width={720} onClose={onFechar}
            title="Revisar a nota da VecchIA"
            description="Nada foi para o prontuário ainda. Confira cada campo, o resumo e, se precisar, a transcrição."
            dirty={mudou}
            onSave={() => void aprovar()} saveLabel="Aprovar e gravar"
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Revisar depois</Button>
                    <Button onClick={() => void aprovar()} loading={aprovando} disabled={!leitura}>Aprovar e gravar no prontuário</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Alert tone="info" dismissible={false}>
                    Texto sugerido pela IA a partir da gravação. A ficha aprovada fica marcada como vinda da IA, com o seu nome.
                </Alert>
                {erroDaLeitura && <Alert tone="danger" dismissible={false}>{erroDaLeitura}</Alert>}
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                <FormField label="Resumo do atendimento" htmlFor={idResumo} required hint="É o que a linha do tempo do prontuário mostra desta consulta.">
                    <Textarea id={idResumo} rows={5} value={resumo} onChange={(v) => { setResumo(v); setErro(null); }} maxLength={20000} />
                </FormField>

                <Divider spacing={2} />

                {!leitura && !erroDaLeitura && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} aria-busy="true">
                        <Skeleton height={16} width="40%" />
                        <Skeleton height={72} />
                        <Skeleton height={72} />
                    </div>
                )}

                {leitura && (
                    <>
                        <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{leitura.nome}</p>
                        {leitura.campos.map((campo) => (
                            <FormField key={campo.uid} label={campo.rotulo} required={campo.obrigatorio}>
                                <BlocoDeResposta
                                    campo={campo}
                                    valor={respostas[campo.uid]}
                                    onResponder={(v) => { setRespostas((atuais) => ({ ...atuais, [campo.uid]: v })); setErro(null); }}
                                    onEnviarArquivo={(arquivo) => dados().enviarAnexoDaFicha(nota.contatoId, campo.uid, arquivo)}
                                    onAbrirArquivo={(caminho) => dados().enderecoDoAnexo(caminho)}
                                />
                            </FormField>
                        ))}

                        <details style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-body)' }}>Ver a transcrição</summary>
                            <p style={{ margin: '10px 0 0', whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
                                {leitura.transcricao || 'Nenhuma fala foi transcrita.'}
                            </p>
                        </details>
                    </>
                )}
            </div>
        </Modal>
    );
}
